import { HelpOutline } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { ReactNode } from 'react';

type SectionHeadingVariant = 'section' | 'page';

const SIZES: Record<SectionHeadingVariant, { badge: number; icon: number; title: string }> = {
  section: { badge: 28, icon: 18, title: '0.9375rem' },
  page: { badge: 32, icon: 20, title: '1.25rem' },
};

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
});

const IconBadge = styled(Box, { shouldForwardProp: property => property !== 'size' })<{
  size: SectionHeadingVariant;
}>(({ theme, size }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SIZES[size].badge,
  height: SIZES[size].badge,
  borderRadius: '6px',
  flexShrink: 0,
  backgroundColor: alpha(theme.palette.secondary.main, 0.14),
  color: theme.palette.secondary.main,
  '& svg': {
    fontSize: SIZES[size].icon,
  },
}));

const Title = styled('span', { shouldForwardProp: property => property !== 'size' })<{
  size: SectionHeadingVariant;
}>(({ theme, size }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: SIZES[size].title,
  lineHeight: 1.2,
}));

const HelpIcon = styled(HelpOutline)(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.text.secondary,
  cursor: 'help',
}));

export const SectionHeading = ({
  icon,
  title,
  tooltip,
  variant = 'section',
}: {
  icon: ReactNode;
  title: string;
  tooltip?: string;
  variant?: SectionHeadingVariant;
}) => (
  <Row>
    <IconBadge size={variant}>{icon}</IconBadge>
    <Title size={variant}>{title}</Title>
    {tooltip && (
      <Tooltip title={tooltip}>
        <HelpIcon />
      </Tooltip>
    )}
  </Row>
);
